import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, ShoppingBag } from 'lucide-react';
import { getCart, removeCartItem, updateCartItem } from '../utils/cartApi';

const safeImageUrl = (url) => {
  if (!url || typeof url !== 'string') return '/placeholder.png';
  const blocked = /(localhost|127\.0\.0\.1|:7071)/i;
  return blocked.test(url) ? '/placeholder.png' : url;
};

const Cart = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadCart = async () => {
    try {
      const cart = await getCart();
      setCartItems(cart.items || []);
    } catch (err) {
      console.error('Load cart failed:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCart();
  }, []);

  const subtotal = cartItems.reduce((a, c) => a + Number(c.price) * Number(c.quantity), 0);
  const total = subtotal;

  const handleRemove = async (productId) => {
    const oldItems = [...cartItems];

    setCartItems(prev => prev.filter(item => item.productId !== productId));
    window.dispatchEvent(new Event('cartUpdated'));

    try {
      await removeCartItem(productId);
    } catch (err) {
      setCartItems(oldItems);
      window.dispatchEvent(new Event('cartUpdated'));
      alert("Failed to remove item");
    }
  };

  const handleQuantityChange = async (productId, newQty) => {
    const oldItems = [...cartItems];
    const currentItem = cartItems.find(item => item.productId === productId);
    const stock = Number(currentItem?.stock || 0);

    if (newQty > stock) {
      alert(`Only ${stock} pieces available in stock`);
      return;
    }

    if (newQty <= 0) {
      setCartItems(prev => prev.filter(item => item.productId !== productId));
    } else {
      setCartItems(prev =>
        prev.map(item =>
          item.productId === productId
            ? { ...item, quantity: newQty }
            : item
        )
      );
    }

    window.dispatchEvent(new Event('cartUpdated'));

    try {
      if (newQty <= 0) {
        await removeCartItem(productId);
      } else {
        await updateCartItem(productId, newQty);
      }
    } catch (err) {
      setCartItems(oldItems);
      window.dispatchEvent(new Event('cartUpdated'));
      alert(err.response?.data?.message || "Failed to update quantity");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cover bg-repeat pb-20 flex flex-col items-center justify-center p-6 md:pb-0" style={{ backgroundImage: "url('/bg-parchment.jpg')" }}>
        <div className="text-[#3d0c11] text-lg font-semibold">Loading cart...</div>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-cover bg-repeat pb-20 flex flex-col items-center justify-center p-6 md:pb-0" style={{ backgroundImage: "url('/bg-parchment.jpg')" }}>
        <ShoppingBag size={64} className="text-[#3d0c11] opacity-20 mb-6" />
        <h2 className="text-[#3d0c11] text-2xl font-serif uppercase tracking-widest mb-4">
          Your Cart is Empty
        </h2>
        <p className="text-gray-500 italic mb-8">
          No masterpieces have been selected yet.
        </p>
        <Link
          to="/explore"
          className="bg-[#3d0c11] text-white px-10 py-4 font-bold uppercase text-[10px] tracking-widest hover:bg-[#d4af37] transition shadow-xl"
        >
          Return to Explore
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cover bg-repeat pb-20 py-16 px-6 md:pb-0" style={{ backgroundImage: "url('/bg-parchment.jpg')" }}>
      <div className="max-w-5xl mx-auto">
        <h1 className="text-[#3d0c11] text-3xl font-serif uppercase tracking-[0.3em] mb-12 text-center">
          Your Selection
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-6">
            {cartItems.map((item, index) => {
              const stock = Number(item.stock || 0);
              const isMaxStock = stock > 0 && Number(item.quantity) >= stock;

              return (
                <div
                  key={`${item.productId || item._id}-${index}`}
                  className="bg-white p-6 shadow-sm flex items-center gap-6 border border-gray-100"
                >
                  <img
                    src={safeImageUrl(item.image)}
                    alt={item.name}
                    className="w-24 h-32 object-cover"
                  />

                  <div className="flex-1">
                    <h3 className="text-[#3d0c11] font-bold uppercase text-xs tracking-widest mb-1">
                      {item.name}
                    </h3>

                    <p className="text-[#8c7b70] text-sm font-serif italic mb-2">
                      ₹ {Number(item.price).toLocaleString()}
                    </p>

                    <p className="text-[10px] text-gray-400 uppercase font-bold tracking-widest">
                      Stock: {stock}
                    </p>

                    <div className="flex items-center gap-3 mt-3">
                      <button
                        onClick={() => handleQuantityChange(item.productId, Number(item.quantity) - 1)}
                        className="w-8 h-8 border border-gray-300 flex items-center justify-center text-[#3d0c11] font-bold"
                      >
                        -
                      </button>

                      <span className="text-sm font-bold text-[#3d0c11]">
                        {item.quantity}
                      </span>

                      <button
                        onClick={() => handleQuantityChange(item.productId, Number(item.quantity) + 1)}
                        disabled={isMaxStock}
                        className={`w-8 h-8 border border-gray-300 flex items-center justify-center font-bold ${
                          isMaxStock
                            ? 'text-gray-300 cursor-not-allowed bg-gray-100'
                            : 'text-[#3d0c11]'
                        }`}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div className="text-right">
                    <p className="text-[#3d0c11] font-bold mb-4">
                      ₹ {(Number(item.price) * Number(item.quantity)).toLocaleString()}
                    </p>

                    <button
                      onClick={() => handleRemove(item.productId)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bg-white p-6 shadow-sm h-fit">
            <h2 className="text-[#3d0c11] font-serif text-xl mb-6 uppercase">
              Order Summary
            </h2>

            <div className="space-y-3 mb-6 border-b pb-6">
              <div className="flex justify-between text-sm">
                <span>Subtotal</span>
                <span>₹ {subtotal.toLocaleString()}</span>
              </div>
            </div>

            <div className="flex justify-between font-bold text-lg mb-8">
              <span>Grand Total</span>
              <span>₹ {total.toLocaleString()}</span>
            </div>

            <button
              onClick={() => navigate('/checkout')}
              className="w-full bg-[#3d0c11] text-white py-4 font-bold uppercase text-[10px] tracking-widest hover:bg-black transition"
            >
              Proceed to Checkout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;